import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

interface Unmapped {
  seen_serial: string;
  last_seen: string;
  sightings: number;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/**
 * Read-only view of BLE tag serials that chassis are sighting but that don't resolve
 * to a truck. The serial->truck# mapping is owned by the safety system
 * (safetynew.truck.blackberryid, keyed by unit number) — so anything listed here needs
 * its blackberryid set on the truck in Safety, not here.
 */
export default function TruckTagMapping() {
  const [unmapped, setUnmapped] = useState<Unmapped[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const u = await api.get('/bb-radar/truck-tags/unmapped');
      setUnmapped(u.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load unmapped serials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-body">Unmapped Truck Tags</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          BLE tag serials that chassis have sighted in the last 7 days but that don&apos;t map to a
          truck. The serial&nbsp;&rarr;&nbsp;truck # link lives in the safety system
          (<code>truck.blackberryid</code>, keyed by unit #). To resolve one of these, set the tag&apos;s
          serial on the matching truck in <strong>Safety</strong> — once it&apos;s there, the
          chassis&nbsp;&harr;&nbsp;truck matcher picks it up automatically.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-3 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
      ) : unmapped.length === 0 ? (
        <p className="text-sm text-muted italic">
          No unmapped serials in the last 7 days.
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">
                  Serial
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">
                  Last Seen
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted uppercase">
                  Sightings
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
              {unmapped.map((u) => (
                <tr key={u.seen_serial}>
                  <td className="px-4 py-2 text-sm text-body">
                    {u.seen_serial}
                  </td>
                  <td className="px-4 py-2 text-sm text-muted">
                    {formatWhen(u.last_seen)}
                  </td>
                  <td className="px-4 py-2 text-sm text-body">
                    {u.sightings}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
